import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Bookmark, Trash2, Eye, LogOut, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Watchlist() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWatchlist = async () => {
      try {
        const res = await fetch(`${API}/watchlist`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setDeals(data.deals || []);
        }
      } catch {}
      setLoading(false);
    };
    fetchWatchlist();
  }, []);

  const removeFromWatchlist = async (dealId) => {
    await fetch(`${API}/watchlist/${dealId}`, { method: 'DELETE', credentials: 'include' });
    setDeals(prev => prev.filter(d => d.deal_id !== dealId));
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-[#1A1A1A]" data-testid="watchlist-page">
      <header className="border-b border-[#A0743A]/20 bg-[#1A1A1A]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="text-[#A0743A] text-lg tracking-[0.15em]" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>SÀWAI CAPITAL</Link>
          <div className="flex items-center gap-6">
            <Link to="/portal" className="text-[#FAF8F5] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-wider" data-testid="nav-deals">Deals</Link>
            {['super_admin','admin','deal_manager','content_manager','investor_relations','view_only'].includes(user?.role) && (
              <Link to="/admin" className="text-[#C9A96E] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-wider">Admin</Link>
            )}
            <div className="flex items-center gap-3 pl-6 border-l border-[#A0743A]/20">
              <span className="text-[#999] text-xs hidden sm:block">{user?.name}</span>
              <button onClick={handleLogout} className="text-[#999] hover:text-[#FAF8F5] transition-colors" data-testid="logout-btn"><LogOut size={16} /></button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        <Link to="/portal" className="flex items-center gap-2 text-[#A0743A] text-sm hover:text-[#C9A96E] transition-colors mb-8" data-testid="back-to-deals">
          <ArrowLeft size={16} /> Back to Deals
        </Link>

        <div className="mb-10">
          <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-2">My Watchlist</p>
          <h1 className="text-3xl text-[#FAF8F5] tracking-tight">Saved Opportunities</h1>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : deals.length === 0 ? (
          <div className="text-center py-20 border border-[#A0743A]/10">
            <Bookmark size={32} className="text-[#A0743A] mx-auto mb-4" strokeWidth={1.25} />
            <p className="text-[#999] mb-2">No deals in your watchlist yet.</p>
            <Link to="/portal" className="text-[#A0743A] text-sm hover:text-[#C9A96E] transition-colors">Browse deals</Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="watchlist-items">
            {deals.map(deal => (
              <div key={deal.deal_id} className="bg-[#FAF8F5] border-l-4 border-[#A0743A] p-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`watchlist-deal-${deal.deal_id}`}>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-[#1A1A1A] text-[#A0743A] px-3 py-1 text-[10px] uppercase tracking-wider font-semibold">{deal.asset_class}</span>
                    <span className="text-[#555] text-xs">{deal.deal_status}</span>
                  </div>
                  <h3 className="text-lg text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>{deal.deal_name}</h3>
                  <p className="text-[#555] text-sm">{deal.target_return} &middot; {deal.deal_size} &middot; {deal.tenor}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Link
                    to={`/portal/deal/${deal.deal_id}`}
                    className="flex items-center gap-2 border border-[#A0743A] text-[#A0743A] px-4 py-2 text-xs uppercase tracking-wider hover:bg-[#A0743A] hover:text-[#FAF8F5] transition-colors"
                    data-testid={`watchlist-view-${deal.deal_id}`}
                  >
                    <Eye size={14} /> View
                  </Link>
                  <button
                    onClick={() => removeFromWatchlist(deal.deal_id)}
                    className="flex items-center gap-2 border border-red-500/30 text-red-400 px-4 py-2 text-xs uppercase tracking-wider hover:bg-red-500/10 transition-colors"
                    data-testid={`watchlist-remove-${deal.deal_id}`}
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
